import { useState, useEffect, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { preloadImages } from 'utils/preloadImages'
import {
  BACKGROUND_IMAGES,
  HOTSPOT_CONFIGS,
  SVG_DIMENSIONS,
  SCREEN_BREAKPOINT
} from './HomeStoryTree.constants'
import type { HotspotId } from './HomeStoryTree.types'
import Url from 'utils/url'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { useQuery } from '@tanstack/react-query'
import { journeysControllerGetMyJourney } from 'api.gen'
import { RUNNING_RECORDS_APP_V2_PATH } from 'routes/paths'

export const useHomeStoryTree = () => {
  const { user } = useUserInfo()

  const { error } = useQuery({
    queryKey: ['myJourney'],
    queryFn: () => journeysControllerGetMyJourney(),
    retry: 0,
    enabled: !!user?.id
  })

  const journeyNotFound = error?.message?.includes('Journey not found')
  const [isLoading, setIsLoading] = useState(true)
  const [loadingError, setLoadingError] = useState<string | null>(null)
  const [isLargeScreen, setIsLargeScreen] = useState(
    window.innerWidth > SCREEN_BREAKPOINT
  )
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const history = useHistory()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadImages = async () => {
      try {
        await preloadImages(BACKGROUND_IMAGES)
        setIsLoading(false)
      } catch (error) {
        setLoadingError('Erro ao carregar imagens')
        setIsLoading(false)
      }
    }

    loadImages()
  }, [])

  const calculatePositions = useCallback(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const containerWidth = container.offsetWidth
    const containerHeight = container.offsetHeight

    const dimensions = isLargeScreen
      ? SVG_DIMENSIONS.LARGE
      : SVG_DIMENSIONS.SMALL
    const { width: svgWidth, height: svgHeight } = dimensions

    const scaleX = containerWidth / svgWidth
    const scaleY = containerHeight / svgHeight
    const actualScale = isLargeScreen
      ? Math.max(scaleX, scaleY)
      : Math.min(scaleX, scaleY)

    const renderedWidth = svgWidth * actualScale
    const renderedHeight = svgHeight * actualScale

    const offsetX = (renderedWidth - containerWidth) / 2
    const offsetY = (renderedHeight - containerHeight) / 2

    setScale(actualScale)
    setOffset({ x: offsetX, y: offsetY })
  }, [isLargeScreen])

  useEffect(() => {
    const handleResize = () => {
      const newIsLargeScreen = window.innerWidth > SCREEN_BREAKPOINT
      setIsLargeScreen(newIsLargeScreen)
      calculatePositions()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [calculatePositions])

  useEffect(() => {
    if (!isLoading) {
      calculatePositions()

      const resizeObserver = new ResizeObserver(calculatePositions)
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [isLoading, calculatePositions])

  const handleHotspotClick = (hotspotId: HotspotId) => {
    const config = HOTSPOT_CONFIGS.find(h => h.id === hotspotId)

    if (config) {
      if (hotspotId === 'storyMaker') {
        Url.set(config.path)
        return
      }

      if (hotspotId === 'journeys' && journeyNotFound) {
        return (window.location.href = `${window.location.origin}${RUNNING_RECORDS_APP_V2_PATH}`)
      }
      history.push(config.path)
    }
  }

  const getAdjustedPosition = useCallback(
    (
      leftPercent: number,
      topPercent: number,
      svgWidth: number,
      svgHeight: number
    ) => {
      const leftPx = (leftPercent / 100) * svgWidth * scale - offset.x
      const topPx = (topPercent / 100) * svgHeight * scale - offset.y

      return {
        left: `${leftPx}px`,
        top: `${topPx}px`
      }
    },
    [scale, offset]
  )

  const getAdjustedWidth = useCallback(
    (widthPercent: number, svgWidth: number) => {
      return {
        width: `${(widthPercent / 100) * svgWidth * scale}px`
      }
    },
    [scale]
  )

  const getAdjustedHeight = useCallback(
    (heightPercent: number, svgHeight: number) => {
      return {
        height: `${(heightPercent / 100) * svgHeight * scale}px`
      }
    },
    [scale]
  )

  return {
    isLoading,
    loadingError,
    handleHotspotClick,
    hotspots: HOTSPOT_CONFIGS,
    isLargeScreen,
    containerRef,
    getAdjustedPosition,
    getAdjustedWidth,
    getAdjustedHeight
  }
}
